import api from './api';

export interface StoreProvisioning {
  tenantId: number;
  tenantCode: string;
  domain?: string;
  storeEnabled: boolean;
  storeUrl?: string;
  adminUrl?: string;
  suggestedSubdomain?: string;
}

export interface StoreProvisioningUpdate {
  domain?: string;
  storeEnabled: boolean;
}

export interface StoreAdminHandoff {
  accessToken: string;
  expiresAt: string;
  tenantCode?: string;
  displayName?: string;
}

function normalize(raw: Record<string, unknown>): StoreProvisioning {
  return {
    tenantId: Number(raw.tenantId ?? raw.tenant_id),
    tenantCode: String(raw.tenantCode ?? raw.tenant_code ?? '').trim(),
    domain: String(raw.domain ?? '').trim() || undefined,
    storeEnabled: raw.storeEnabled !== undefined ? Boolean(raw.storeEnabled) : Boolean(raw.store_enabled ?? false),
    storeUrl: String(raw.storeUrl ?? raw.store_url ?? '').trim() || undefined,
    adminUrl: String(raw.adminUrl ?? raw.admin_url ?? '').trim() || undefined,
    suggestedSubdomain: String(raw.suggestedSubdomain ?? raw.suggested_subdomain ?? '').trim() || undefined,
  };
}

const storeProvisioningService = {
  getCurrent: async (): Promise<StoreProvisioning> => {
    const { data } = await api.get('/api/admin/store-provisioning');
    return normalize(data as Record<string, unknown>);
  },

  updateCurrent: async (payload: StoreProvisioningUpdate): Promise<StoreProvisioning> => {
    const { data } = await api.put('/api/admin/store-provisioning', {
      domain: payload.domain?.trim() || undefined,
      storeEnabled: payload.storeEnabled,
    });
    return normalize(data as Record<string, unknown>);
  },

  getByTenant: async (tenantId: number): Promise<StoreProvisioning> => {
    const { data } = await api.get(`/api/admin/tenants/${tenantId}/store-provisioning`);
    return normalize(data as Record<string, unknown>);
  },

  updateByTenant: async (tenantId: number, payload: StoreProvisioningUpdate): Promise<StoreProvisioning> => {
    const { data } = await api.put(`/api/admin/tenants/${tenantId}/store-provisioning`, {
      domain: payload.domain?.trim() || undefined,
      storeEnabled: payload.storeEnabled,
    });
    return normalize(data as Record<string, unknown>);
  },

  createAdminHandoff: async (): Promise<StoreAdminHandoff> => {
    const { data } = await api.post('/api/admin/store-provisioning/admin-handoff');
    const raw = data as Record<string, unknown>;
    return {
      accessToken: String(raw.accessToken ?? raw.access_token ?? ''),
      expiresAt: String(raw.expiresAt ?? raw.expires_at ?? ''),
      tenantCode: String(raw.tenantCode ?? raw.tenant_code ?? '').trim() || undefined,
      displayName: String(raw.displayName ?? raw.display_name ?? '').trim() || undefined,
    };
  },
};

export default storeProvisioningService;
